/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { hpApi } from './hpApi.js';

// ---------- アイコン ----------

const base = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };

export const HpIcon = {
  Plus: ({ size = 16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" {...base}><path d="M12 5v14M5 12h14" /></svg>),
  Edit: ({ size = 16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" {...base}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>),
  Trash: ({ size = 16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" {...base}><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /></svg>),
  Up: ({ size = 16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" {...base}><path d="m18 15-6-6-6 6" /></svg>),
  Down: ({ size = 16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" {...base}><path d="m6 9 6 6 6-6" /></svg>),
  External: ({ size = 14 }) => (<svg width={size} height={size} viewBox="0 0 24 24" {...base}><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></svg>),
  Video: ({ size = 16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" {...base}><rect x="2" y="5" width="15" height="14" rx="2" /><path d="m17 10 5-3v10l-5-3" /></svg>),
  Image: ({ size = 16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" {...base}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-5-5L5 21" /></svg>),
  Eye: ({ size = 16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" {...base}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></svg>),
  Back: ({ size = 16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" {...base}><path d="m15 18-6-6 6-6" /></svg>),
  Check: ({ size = 16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" {...base}><path d="M20 6 9 17l-5-5" /></svg>),
  Alert: ({ size = 16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" {...base}><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>),
  Close: ({ size = 14 }) => (<svg width={size} height={size} viewBox="0 0 24 24" {...base}><path d="M18 6 6 18M6 6l12 12" /></svg>),
  Lock: ({ size = 14 }) => (<svg width={size} height={size} viewBox="0 0 24 24" {...base}><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>),
};

// ---------- トースト ----------

const ToastContext = createContext(null);

export function HpToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const seq = useRef(0);

  const dismiss = useCallback((id) => setToasts((list) => list.filter((t) => t.id !== id)), []);
  const push = useCallback((type, message) => {
    const id = ++seq.current;
    setToasts((list) => [...list.slice(-3), { id, type, message }]);
    setTimeout(() => dismiss(id), type === 'error' ? 7000 : 3500);
  }, [dismiss]);

  const value = useMemo(() => ({
    success: (m) => push('success', m),
    error: (m) => push('error', m),
    info: (m) => push('info', m),
  }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="hp-toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`hp-toast hp-toast--${t.type}`}>
            <span className="hp-toast__icon">{t.type === 'error' ? <HpIcon.Alert /> : <HpIcon.Check />}</span>
            <span className="hp-toast__msg">{t.message}</span>
            <button type="button" className="hp-toast__close" onClick={() => dismiss(t.id)} aria-label="閉じる"><HpIcon.Close /></button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useHpToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useHpToast must be used within HpToastProvider');
  return ctx;
}

// ---------- ドロップダウン ----------

export function HpSelect({ value, options, onChange, ariaLabel, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const rootRef = useRef(null);
  const listId = useId();
  const current = options.find((o) => o.value === value) || options[0];

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!rootRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const choose = (opt) => {
    onChange(opt.value);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); setActive(options.indexOf(current)); return; }
      setActive((i) => (i + (e.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (open && active >= 0) choose(options[active]);
      else { setOpen(true); setActive(options.indexOf(current)); }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className={`hp-select${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="hp-select__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => { setOpen((v) => !v); setActive(options.indexOf(current)); }}
        onKeyDown={onKeyDown}
      >
        <span>{current?.label}</span>
        <HpIcon.Down size={14} />
      </button>
      {open && (
        <ul className="hp-select__list" role="listbox" id={listId}>
          {options.map((opt, i) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              className={`hp-select__option${opt.value === value ? ' is-selected' : ''}${i === active ? ' is-active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => { e.preventDefault(); choose(opt); }}
            >
              <span>{opt.label}</span>
              {opt.value === value && <HpIcon.Check size={14} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- 切替スイッチ ----------

export function HpSwitch({ checked, onChange, label, description, disabled = false }) {
  return (
    <label className={`hp-switch${disabled ? ' is-disabled' : ''}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="hp-switch__track" aria-hidden="true"><span className="hp-switch__thumb" /></span>
      <span className="hp-switch__text">
        <span className="hp-switch__label">{label}</span>
        {description && <span className="hp-switch__desc">{description}</span>}
      </span>
    </label>
  );
}

// ---------- 入力欄 ----------

export function HpField({ label, hint, required, children, counter }) {
  return (
    <div className="hp-field">
      <div className="hp-field__head">
        <span className="hp-field__label">{label}{required && <em className="hp-field__req">必須</em>}</span>
        {counter}
      </div>
      {children}
      {hint && <p className="hp-field__hint">{hint}</p>}
    </div>
  );
}

export function HpCounter({ value, max }) {
  const n = (value || '').length;
  return <span className={`hp-counter${n > max ? ' is-over' : ''}`}>{n}/{max}</span>;
}

// ---------- 画像 ----------

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';

export function hpImageSrc(url, siteUrl) {
  if (!url) return '';
  if (url.startsWith('/')) return `${(siteUrl || '').replace(/\/$/, '')}${url}`;
  return url;
}

export function HpImageField({ value, onChange, folder, siteUrl, disabled, aspect = '4 / 5', emptyLabel = '画像未設定' }) {
  const toast = useHpToast();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('ファイルサイズは5MB以下にしてください');
      return;
    }
    setUploading(true);
    try {
      const res = await hpApi.uploadImage(folder, file);
      onChange(res.url);
      toast.success('画像をアップロードしました（保存すると反映されます）');
    } catch (err) {
      toast.error(err.message || '画像のアップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="hp-image">
      <div className="hp-image__preview" style={{ aspectRatio: aspect }}>
        {value ? <img src={hpImageSrc(value, siteUrl)} alt="" /> : <span className="hp-image__empty"><HpIcon.Image size={22} />{emptyLabel}</span>}
        {uploading && <span className="hp-image__busy">アップロード中…</span>}
      </div>
      <div className="hp-image__actions">
        <button type="button" className="hp-btn hp-btn--ghost" disabled={disabled || uploading} onClick={() => inputRef.current?.click()}>
          <HpIcon.Image size={14} />{value ? '画像を差し替え' : '画像をアップロード'}
        </button>
        {value && (
          <button type="button" className="hp-btn hp-btn--text" disabled={disabled || uploading} onClick={() => onChange('')}>外す</button>
        )}
        <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={onFile} />
      </div>
      <p className="hp-field__hint">JPEG・PNG・WebP・GIF／5MB以下</p>
    </div>
  );
}

// ---------- 動画 ----------

const VIDEO_ACCEPT = 'video/mp4,video/webm';
const VIDEO_MAX_SIZE = 30 * 1024 * 1024;

export function HpVideoField({ value, poster, onChange, siteUrl, disabled }) {
  const toast = useHpToast();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > VIDEO_MAX_SIZE) {
      toast.error('ファイルサイズは30MB以下にしてください');
      return;
    }
    setUploading(true);
    try {
      const res = await hpApi.uploadVideo(file);
      onChange(res.url);
      toast.success('動画をアップロードしました（保存すると反映されます）');
    } catch (err) {
      toast.error(err.message || '動画のアップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="hp-image">
      <div className="hp-image__preview hp-video__preview" style={{ aspectRatio: '16 / 9' }}>
        {value
          ? <video src={hpImageSrc(value, siteUrl)} poster={hpImageSrc(poster, siteUrl) || undefined} controls playsInline preload="metadata" />
          : <span className="hp-image__empty"><HpIcon.Video size={22} />動画未設定</span>}
        {uploading && <span className="hp-image__busy">アップロード中…</span>}
      </div>
      <div className="hp-image__actions">
        <button type="button" className="hp-btn hp-btn--ghost" disabled={disabled || uploading} onClick={() => inputRef.current?.click()}>
          <HpIcon.Video size={14} />{value ? '動画を差し替え' : '動画をアップロード'}
        </button>
        {value && (
          <button type="button" className="hp-btn hp-btn--text" disabled={disabled || uploading} onClick={() => onChange('')}>外す</button>
        )}
        <input ref={inputRef} type="file" accept={VIDEO_ACCEPT} hidden onChange={onFile} />
      </div>
      <p className="hp-field__hint">MP4（H.264）・WebM／30MB以下。音声付きでも HP では自動再生しません</p>
    </div>
  );
}

// ---------- 状態バッジ ----------

export function HpBadge({ tone = 'muted', children }) {
  return <span className={`hp-badge hp-badge--${tone}`}>{children}</span>;
}

// ---------- 未保存の離脱防止 ----------

export function useUnsavedGuard(dirty) {
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);
}
